import { motion } from 'framer-motion';

import { themes, useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';

export default function InvestmentSection() {
  const { currentTheme } = useTheme();
  const isMobile = useIsMobile();

  return (
    <section
      className="relative min-h-[80vh] w-full overflow-hidden px-4 py-32 transition-colors duration-700 md:px-8"
      style={{
        background: themes[currentTheme].bgGradient,
        color: themes[currentTheme].text,
      }}
    >
      {/* Animated Background - Show animated on desktop, static on mobile */}
      <div className="absolute inset-0 z-[1]">
        <div className="relative size-full">
          {!isMobile ? (
            <>
              <motion.div
                className="absolute -left-1/4 top-1/4 size-72 rounded-full bg-gradient-to-r from-[#4F46E5] to-[#9333EA] opacity-60 blur-[120px]"
                animate={{
                  scale: [1, 1.2, 1],
                  x: [0, 50, 0],
                  y: [0, -40, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  willChange: 'transform',
                }}
              />
              <motion.div
                className="absolute -right-1/4 top-1/2 size-96 rounded-full bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] opacity-50 blur-[120px]"
                animate={{
                  scale: [1.2, 1, 1.2],
                  x: [0, -60, 0],
                  y: [0, 40, 0],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  willChange: 'transform',
                }}
              />
              <motion.div
                className="absolute -bottom-1/4 left-2/3 size-80 rounded-full bg-gradient-to-r from-[#2563EB] to-[#D946EF] opacity-60 blur-[120px]"
                animate={{
                  scale: [1, 1.3, 1],
                  x: [0, 30, 0],
                  y: [0, -30, 0],
                }}
                transition={{
                  duration: 9,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  willChange: 'transform',
                }}
              />
            </>
          ) : (
            <>
              <div className="absolute left-[15%] top-[15%] size-40 rounded-full bg-gradient-to-r from-[#4F46E5] to-[#8B5CF6] opacity-25 blur-[50px]" />
              <div className="absolute bottom-[20%] right-[15%] size-48 rounded-full bg-gradient-to-r from-[#EC4899] to-[#9333EA] opacity-20 blur-[60px]" />
            </>
          )}
        </div>
      </div>

      {/* Floating Elements - Simplified version for mobile */}
      <div className="absolute inset-0 overflow-hidden">
        {!isMobile ? (
          <>
            <motion.div
              className="absolute -left-4 top-32 size-6 rounded-full sm:size-8"
              style={{ background: themes[currentTheme].gradient }}
              animate={{
                y: [0, 10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute -right-2 top-48 size-4 rounded-full sm:size-6"
              style={{ background: themes[currentTheme].gradient }}
              animate={{
                x: [0, 15, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute -bottom-4 right-1/4 size-8 rounded-full opacity-30 sm:size-10"
              style={{ background: themes[currentTheme].gradient }}
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </>
        ) : (
          <>
            <motion.div
              className="absolute left-8 top-24 size-3 rounded-full"
              style={{ background: themes[currentTheme].gradient }}
              animate={{
                y: [0, 8, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute right-12 top-48 size-2 rounded-full opacity-80"
              style={{ background: themes[currentTheme].gradient }}
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </>
        )}
      </div>

      <div className="relative z-[2] mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-16"
        >
          <div className="space-y-8">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold md:text-6xl"
            >
              Not only do we invest, but we&apos;re{' '}
              <span className={`text-gradient-${currentTheme}`}>
                builders ourselves
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xl md:text-2xl"
              style={{ color: themes[currentTheme].textMuted }}
            >
              We invest in visions.
            </motion.p>
            <div className="grid gap-12 md:grid-cols-2">
              <motion.div
                className="space-y-4 rounded-2xl bg-black/5 p-8 backdrop-blur-lg"
                whileHover={!isMobile ? { scale: 1.02 } : undefined}
                transition={{ type: 'spring', stiffness: 300 }}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <p
                  className="text-lg md:text-xl"
                  style={{ color: themes[currentTheme].textMuted }}
                >
                  Offering collaborative capital, we&apos;re the trusted ally
                  for founders at stages when others hesitate.
                </p>
              </motion.div>
              <motion.div
                className="space-y-4 rounded-2xl bg-black/5 p-8 backdrop-blur-lg"
                whileHover={!isMobile ? { scale: 1.02 } : undefined}
                transition={{ type: 'spring', stiffness: 300 }}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <p
                  className="text-lg md:text-xl"
                  style={{ color: themes[currentTheme].textMuted }}
                >
                  Our commitment is to the technology-first, creative thinkers
                  who dare to solve the toughest problems.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
